import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { checkEncryptionHealth } from '@/lib/encryption-health-check';
import { useAuth } from './AuthContext';

interface EncryptionContextType {
  isEncryptionReady: boolean;
  isCheckingEncryption: boolean;
  encryptionError: string | null;
}

const EncryptionContext = createContext<EncryptionContextType | undefined>(undefined);

export const useEncryption = () => {
  const context = useContext(EncryptionContext);
  if (!context) {
    throw new Error('useEncryption must be used within EncryptionProvider');
  }
  return context;
};

interface EncryptionProviderProps {
  children: ReactNode;
}

export const EncryptionProvider = ({ children }: EncryptionProviderProps) => {
  const [isEncryptionReady, setIsEncryptionReady] = useState(false);
  const [isCheckingEncryption, setIsCheckingEncryption] = useState(false);
  const [encryptionError, setEncryptionError] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    // Só executar health check se o usuário estiver autenticado
    if (!user) {
      console.log('[EncryptionContext] No user - skipping encryption check');
      setIsCheckingEncryption(false);
      setIsEncryptionReady(false);
      setEncryptionError(null);
      return;
    }

    const initializeEncryption = async () => {
      console.log('[EncryptionContext] Starting encryption health check...');
      setIsCheckingEncryption(true);
      
      try {
        const isHealthy = await checkEncryptionHealth(3);
        
        if (isHealthy) {
          console.log('[EncryptionContext] ✅ Encryption is ready');
          setIsEncryptionReady(true);
          setEncryptionError(null);
        } else {
          console.error('[EncryptionContext] ❌ Encryption health check failed');
          setIsEncryptionReady(false);
          setEncryptionError('Sistema de criptografia indisponível. Verifique sua conexão e tente novamente.');
        }
      } catch (error) {
        console.error('[EncryptionContext] ❌ Exception during encryption check:', error);
        setIsEncryptionReady(false);
        setEncryptionError('Erro ao verificar criptografia');
      } finally {
        setIsCheckingEncryption(false);
      }
    };

    initializeEncryption();
  }, [user]);

  return (
    <EncryptionContext.Provider value={{ isEncryptionReady, isCheckingEncryption, encryptionError }}>
      {children}
    </EncryptionContext.Provider>
  );
};
